import { useCallback } from "react";
import { createContext, useEffect, useMemo, useState } from "react";

import takeMonth, { getYearInterval } from "../utils/calendar.utils";

const CALENDAR_VIEW_TYPES = {
	year: "year",
	month: "month",
	day: "day",
};

export const CalendarContext = createContext({
	calendar: [],
	calendarViewType: "",
	setCalendarViewType: () => {},
	Prev: () => {},
	Next: () => {},
	changeDay: () => {},
	changeMonth: () => {},
	changeYear: () => {},
	goToToday: () => {},
	showSelectedDay: () => {},
	changeView: () => {},
});

const CalendarProvider = ({ children }) => {
	const [calendar, setCalendar] = useState([]);
	const [selectedDay, setSelectedDay] = useState(new Date());
	const [shownMonth, setShownMonth] = useState(selectedDay.getMonth());
	const [shownYear, setShownYear] = useState(selectedDay.getFullYear());
	const [shownYearsInterval, setShownYearsInterval] = useState(getYearInterval(shownYear));
	const [calendarViewType, setCalendarViewType] = useState("day");

	useEffect(() => {
		const date = new Date(shownYear, shownMonth, 1, 0, 0, 0, 0);
		const tm = takeMonth(date);
		setCalendar(tm());
		setShownYearsInterval(getYearInterval(shownYear));
	}, [shownMonth, shownYear]);

	useEffect(() => {
		setShownMonth(selectedDay.getMonth());
		setShownYear(selectedDay.getFullYear());
	}, [selectedDay]);

	const Prev = useCallback(() => {
		switch (calendarViewType) {
			case CALENDAR_VIEW_TYPES.day:
				if (shownMonth === 0) setShownYear((oldYear) => oldYear - 1);
				setShownMonth((oldMonth) => (oldMonth + 11) % 12);

				break;
			case CALENDAR_VIEW_TYPES.month:
				setShownYear((oldYear) => oldYear - 1);

				break;
			case CALENDAR_VIEW_TYPES.year:
				setShownYearsInterval((oldInterval) => [oldInterval[0] - 10, oldInterval[1] - 10]);

				break;
			default:
				break;
		}
	}, [calendarViewType, shownMonth]);

	const Next = useCallback(() => {
		switch (calendarViewType) {
			case CALENDAR_VIEW_TYPES.day:
				if (shownMonth === 11) setShownYear((oldYear) => oldYear + 1);
				setShownMonth((oldMonth) => (oldMonth + 1) % 12);

				break;
			case CALENDAR_VIEW_TYPES.month:
				setShownYear((oldYear) => oldYear + 1);
				break;
			case CALENDAR_VIEW_TYPES.year:
				setShownYearsInterval((oldInterval) => [oldInterval[0] + 10, oldInterval[1] + 10]);

				break;
			default:
				break;
		}
	}, [calendarViewType, shownMonth]);

	const changeDay = useCallback((day) => {
		setSelectedDay(day);
	}, []);

	const changeMonth = useCallback((month, year) => {
		setShownMonth(month);
		setShownYear(year);
		// setCalendarViewType(CALENDAR_VIEW_TYPES.day);
	}, []);

	const changeYear = useCallback((year) => {
		setShownYear(year);
		// setCalendarViewType(CALENDAR_VIEW_TYPES.month);
	}, []);

	const goToToday = useCallback(() => {
		setSelectedDay(new Date());
	}, []);

	const showSelectedDay = useCallback(() => {
		setShownMonth(selectedDay.getMonth());
		setShownYear(selectedDay.getFullYear());
		setCalendarViewType(CALENDAR_VIEW_TYPES.day);
	}, [selectedDay]);

	const changeView = useCallback(() => {
		switch (calendarViewType) {
			case CALENDAR_VIEW_TYPES.day:
				setCalendarViewType(CALENDAR_VIEW_TYPES.month);
				break;
			case CALENDAR_VIEW_TYPES.month:
				setCalendarViewType(CALENDAR_VIEW_TYPES.year);
				break;
			default:
				break;
		}
	}, [calendarViewType]);

	const value = useMemo(
		() => ({
			calendar,
			selectedDay,
			shownMonth,
			shownYear,
			shownYearsInterval,
			calendarViewType,
			setCalendarViewType,
			Prev,
			Next,
			changeDay,
			changeMonth,
			changeYear,
			goToToday,
			showSelectedDay,
			changeView,
		}),
		[
			calendar,
			selectedDay,
			shownMonth,
			shownYear,
			shownYearsInterval,
			calendarViewType,
			setCalendarViewType,
			Prev,
			Next,
			changeDay,
			changeMonth,
			changeYear,
			goToToday,
			showSelectedDay,
			changeView,
		]
	);

	return <CalendarContext.Provider value={value}>{children}</CalendarContext.Provider>;
};

export default CalendarProvider;
